/* @flow */

import './MainLayout.css';
import '../../ku.js';
import '../../ku.css';
import clsx from 'clsx';
import i18next from 'i18next';
import log from 'loglevel';
import * as React from 'react';
import { connect } from 'react-redux';

import type { AllSettledPromises } from '../../helpers/jsHelpers/promise';
import type { BasicFunction } from '../../helpers/jsHelpers/types';
import type { SettingValue } from '../../helpers/settings/types';
import type { FeedRequestResponseType } from '../../libs/netgemLibrary/v8/requests/feed';
import type { NETGEM_API_V8_DEEPLINK_RESPONSE } from '../../libs/netgemLibrary/v8/types/Deeplink';
import type { FeedRequestFunction } from '../../libs/netgemLibrary/v8/types/Feed';
import type { NETGEM_API_V8_HUB } from '../../libs/netgemLibrary/v8/types/Hub';
import type { NETGEM_API_V8_METADATA } from '../../libs/netgemLibrary/v8/types/MetadataProgram';
import type { NETGEM_API_V8_URL_DEFINITION } from '../../libs/netgemLibrary/v8/types/NtgVideoFeed';
import type { NETGEM_API_V8_REQUEST_RESPONSE } from '../../libs/netgemLibrary/v8/types/RequestResponse';
import type { ModalState } from '../../redux/modal/reducers';
import type { CombinedReducers } from '../../redux/reducers';
import type { Dispatch } from '../../redux/types/types';
import type { VideoCarouselStateType } from '../../redux/ui/types/types';
import type { AVENUE_DATA_MODAL_TYPE } from '../modal/avenueModal/AvenueModal';
import type { CARD_DATA_MODAL_TYPE } from '../modal/cardModal/CardModalConstsAndTypes';
import type { ShakaOfflineContent } from '../player/implementation/shakaTypes';

import { showDebug as showChargebeeDebug } from '../../helpers/applicationCustomization/externalPaymentSystem';
import { APPLICATION_ID } from '../../helpers/applicationCustomization/types';
import AccurateTimestamp from '../../helpers/dateTime/AccurateTimestamp';
import { MILLISECONDS_PER_WEEK } from '../../helpers/dateTime/Format';
import { logDebug, logError, logInfo, logWarning } from '../../helpers/debug/debug';
import { showEE } from '../../helpers/debug/ee';
import HotKeys from '../../helpers/hotKeys/hotKeys';
import Localizer from '../../helpers/localization';
import { StorageKeys } from '../../helpers/localStorage/keys';
import LocalStorageManager from '../../helpers/localStorage/localStorageManager';
import { Messenger, MessengerEvents } from '../../helpers/messenger';
import { hasPendingOperation } from '../../helpers/rights/pendingOperations';
import { hasPods } from '../../helpers/rights/userInfo';
import { saveSearchString } from '../../helpers/search/search';
import { Setting } from '../../helpers/settings/types';
import { getAvenueFromId } from '../../helpers/ui/avenue/helpers';
import { AvenueType, type FocusedAvenue } from '../../helpers/ui/avenue/types';
import { isTabHidden, openFooterPage } from '../../helpers/ui/browser';
import { getOnItemClick } from '../../helpers/ui/item/traverser';
import { type EXTENDED_ITEM, ExtendedItemType } from '../../helpers/ui/item/types';
import { ignoreIfAborted } from '../../libs/netgemLibrary/helpers/cancellablePromise/promiseHelper';
import { ItemLocationKind, ItemType, type NETGEM_API_V8_FEED_ITEM } from '../../libs/netgemLibrary/v8/types/FeedItem';
import { METADATA_KIND_PROGRAM, METADATA_KIND_SERIES, type MetadataKind } from '../../libs/netgemLibrary/v8/types/MetadataProgram';
import { type NetgemApiV8WidgetItemClick, NetgemApiV8WidgetItemClickActionKind } from '../../libs/netgemLibrary/v8/types/WidgetConfig';
import { FEATURE_NPVR } from '../../redux/appConf/constants';
import { RegistrationType, type UserInfo } from '../../redux/appRegistration/types/types';
import { hideModal, reopenModalCard, showAvenueModal } from '../../redux/modal/actions';
import { clearWishlist } from '../../redux/netgemApi/actions/personalData/wishlist';
import sendV8LocationCatchupForAssetRequest from '../../redux/netgemApi/actions/v8/catchupForAsset';
import sendV8CustomStrategyRequest from '../../redux/netgemApi/actions/v8/customStrategyRequest';
import sendV8AssetIdForDeeplinkRequest from '../../redux/netgemApi/actions/v8/deeplink';
import sendV8LocationEpgForAssetRequest from '../../redux/netgemApi/actions/v8/epgForAsset';
import { getValidHub, sendV8HubRequest } from '../../redux/netgemApi/actions/v8/hub';
import sendV8MetadataRequest from '../../redux/netgemApi/actions/v8/metadata';
import search from '../../redux/netgemApi/actions/v8/search';
import sendV8LocationVodForAssetRequest from '../../redux/netgemApi/actions/v8/vodForAsset';
import {
  enterTheaterMode,
  exitTheaterMode,
  navigateToAvenue,
  resetGridSectionId,
  resetSectionPageIndices,
  updateDisplayPaywallSubscription,
  updateSetting,
  updateVideoCarouselPaused,
  updateVideoCarouselPlaying,
} from '../../redux/ui/actions';
import { getMainAvenue } from '../../redux/ui/reducers/helpers';
import AvenueView from '../avenue/Avenue';
import ImageCarousel from '../carousel/ImageCarousel';
import VideoCarousel from '../carousel/VideoCarousel';
import ErrorBoundary from '../errorBoundary/ErrorBoundary';
import Footer from '../footer/Footer';
import Header from '../header/Header';
import LandmarkNavigation from '../landmarkNavigation/LandmarkNavigation';
import { NO_AVENUE_INDEX } from '../navigationMenu/NavigationMenu';
import Player from '../player/Player';
import { getItem } from './helper';

const NpvrManagement = React.lazy(() => import(/* webpackPrefetch: true */ '../npvrManagement/NpvrManagement'));
const AppSettings = React.lazy(() => import(/* webpackPrefetch: true */ '../settings/AppSettings'));

// Delay before actually restoring vertical scroll position (because we have to wait a little before everything is displayed)
const VERTICAL_POSITION_RESTORE_DELAY = 200;

// Vertical scroll position from which the header becomes opaque
const SCROLL_THRESHOLD = 400;

type ReduxMainLayoutDispatchToPropsType = {
  +clearDisplayPaywallSubscription: BasicFunction,
  +localClearWishlist: () => Promise<void>,
  +localEnterTheaterMode: BasicFunction,
  +localExitTheaterMode: BasicFunction,
  +localHideModal: (shouldBeReopenedLater?: boolean) => void,
  +localNavigateToAvenue: (index: number, type: AvenueType, hubItem?: NETGEM_API_V8_FEED_ITEM, hub?: NETGEM_API_V8_HUB, searchString?: string, isSearchFocusHidden?: boolean) => void,
  +localReopenModalCard: BasicFunction,
  +localResetGridSectionId: BasicFunction,
  +localResetSectionPageIndices: BasicFunction,
  +localSearch: (signal?: AbortSignal) => Promise<NETGEM_API_V8_HUB>,
  +localSendV8AssetIdForDeeplinkRequest: (type: ItemType, deeplinkParameter: string, signal?: AbortSignal) => Promise<NETGEM_API_V8_DEEPLINK_RESPONSE>,
  +localSendV8CustomStrategyRequest: (urlDefinition: NETGEM_API_V8_URL_DEFINITION, item: NETGEM_API_V8_FEED_ITEM, signal?: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE>,
  +localSendV8HubRequest: (hubKey: string, signal?: AbortSignal) => Promise<NETGEM_API_V8_HUB>,
  +localSendV8LocationCatchupForAssetRequest: FeedRequestFunction,
  +localSendV8LocationEpgForAssetRequest: FeedRequestFunction,
  +localSendV8LocationVodForAssetRequest: FeedRequestFunction,
  +localSendV8MetadataRequest: (assetId: string, type: MetadataKind, signal?: AbortSignal) => Promise<NETGEM_API_V8_METADATA>,
  +localShowAvenueModal: (avenueData: AVENUE_DATA_MODAL_TYPE, ariaLabel: string) => void,
  +localUpdateSetting: (setting?: Setting, value: SettingValue) => void,
  +localUpdateVideoCarouselPaused: BasicFunction,
  +localUpdateVideoCarouselPlaying: BasicFunction,
};

type ReduxMainLayoutReducerStateType = {
  +avenueList: NETGEM_API_V8_HUB | null,
  +deeplink: ?string,
  +defaultOnItemClick: ?NetgemApiV8WidgetItemClick,
  +displaySubscriptionPaywall: boolean,
  +focusedAvenue: FocusedAvenue | null,
  +guruWebsiteUrl: string,
  +isDebugModeEnabled: boolean,
  +isInTheaterMode: boolean,
  +isNpvrFeatureEnabled: boolean,
  +isRegisteredAsGuest: boolean,
  +openModals: Array<ModalState>,
  +userInfo: ?UserInfo,
  +videoCarousel: VideoCarouselStateType,
};

type MainLayoutPropType = {
  +hideContent: boolean,
};

type CompleteMainLayoutPropType = {
  ...MainLayoutPropType,
  ...ReduxMainLayoutDispatchToPropsType,
  ...ReduxMainLayoutReducerStateType,
};

type MainLayoutStateType = {
  child: Array<React.Node>,
  isHeaderLoaded: boolean,
  isScrolling: boolean,
  playerItem: EXTENDED_ITEM | null,
  playerOfflineContent: ShakaOfflineContent | null,
  previousVerticalPosition: { key: string, scroll: number } | null,
};

const InitialState: Readonly<MainLayoutStateType> = Object.freeze({
  child: [],
  isHeaderLoaded: false,
  isScrolling: false,
  playerItem: null,
  playerOfflineContent: null,
  previousVerticalPosition: null,
});

const LoadedHeaderState: MainLayoutStateType = {
  ...InitialState,
  isHeaderLoaded: true,
};

class MainLayoutView extends React.PureComponent<CompleteMainLayoutPropType, MainLayoutStateType> {
  abortController: AbortController;

  exploreAvenueIndex: number | null;

  isDeeplinkHandled: boolean;

  isSectionSliding: boolean;

  mainLayout: HTMLElement | null;

  constructor(props: CompleteMainLayoutPropType) {
    super(props);

    this.abortController = new AbortController();
    this.exploreAvenueIndex = null;
    this.isDeeplinkHandled = false;
    this.isSectionSliding = false;

    this.state = { ...InitialState };
  }

  componentDidMount() {
    const { avenueList, focusedAvenue, localNavigateToAvenue } = this.props;

    // Tell application container that app is loaded (used to prevent from showing loading animation again)
    Messenger.emit(MessengerEvents.APPLICATION_FULLY_LOADED);

    Messenger.on(MessengerEvents.AVENUE_RESET, this.moveToTop);
    Messenger.on(MessengerEvents.ITEM_CLICKED, this.executeItemClick);
    Messenger.on(MessengerEvents.MANAGE_SUPER_STREAM, this.manageSuperStream);
    Messenger.on(MessengerEvents.MOVE_TO_TOP, this.moveToTop);
    Messenger.on(MessengerEvents.OPEN_EXPLORE_MODAL, this.openExploreModal);
    Messenger.on(MessengerEvents.OPEN_GLOBAL_SETTINGS, this.openGlobalSettingsScreen);
    Messenger.on(MessengerEvents.OPEN_NPVR_MANAGEMENT_SCREEN, this.openNpvrManagementScreen);
    Messenger.on(MessengerEvents.OPEN_PLAYER, this.openPlayer);
    Messenger.on(MessengerEvents.OPEN_TILE_TYPES_AVENUE, this.openTileTypesAvenue);
    Messenger.on(MessengerEvents.OPEN_GRID_AVENUE, this.openGridAvenue);
    Messenger.on(MessengerEvents.RESTORE_POSITION, this.restorePosition);
    Messenger.on(MessengerEvents.SECTION_SLIDING_UPDATE, this.updateSectionSliding);

    if (document !== undefined) {
      document.addEventListener('visibilitychange', this.checkVisibility, { passive: true });
    }

    this.setDebugFunctions();

    // Load focused avenue at startup (either main avenue if specified or first avenue otherwise)
    if (focusedAvenue) {
      const { index, searchString, type } = focusedAvenue;

      if (type === AvenueType.Search) {
        // Case when user signs in while search avenue is open
        if (searchString) {
          // Valid search string
          this.handleOnSearch(searchString);
        } else {
          // Display main avenue as a fallback
          const mainAvenue = getMainAvenue(avenueList);
          localNavigateToAvenue(mainAvenue?.index ?? 0, mainAvenue?.type ?? AvenueType.Regular);
        }
      } else {
        // All other cases
        this.loadAvenue(index, type);
      }
    }
  }

  componentDidUpdate(prevProps: CompleteMainLayoutPropType) {
    const { focusedAvenue, isDebugModeEnabled, isInTheaterMode, localResetSectionPageIndices } = this.props;
    const { focusedAvenue: prevFocusedAvenue, isDebugModeEnabled: prevIsDebugModeEnabled, isInTheaterMode: prevIsInTheaterMode } = prevProps;

    if (isDebugModeEnabled !== prevIsDebugModeEnabled) {
      this.setDebugFunctions();
    }

    if (isInTheaterMode !== prevIsInTheaterMode) {
      if (!isInTheaterMode) {
        this.handleTheaterModeExited();
      }
    }

    if (focusedAvenue && prevFocusedAvenue) {
      const { hub, hubItem, index, searchString, type } = focusedAvenue;
      const { hub: prevHub, hubItem: prevHubItem, index: prevIndex, searchString: prevSearchString } = prevFocusedAvenue;

      if (focusedAvenue !== prevFocusedAvenue && ((index !== NO_AVENUE_INDEX && (index !== prevIndex || hubItem !== prevHubItem || hub !== prevHub)) || type === AvenueType.Search)) {
        // Avenue change: load new avenue

        // Reset saved positions for all sections, except when the same universe avenue is reloaded (after exiting the player or when coming back from a hidden avenue, for instance)
        if (searchString !== prevSearchString || ((index !== prevIndex || type === AvenueType.Explore) && (!hubItem || hubItem?.id !== prevHubItem?.id))) {
          localResetSectionPageIndices();
        }

        this.loadAvenue(index, type, hub !== undefined && type !== AvenueType.Explore && type !== AvenueType.Search, hubItem, hub);
      }
    }
  }

  componentWillUnmount() {
    const { abortController } = this;

    abortController.abort('Component MainLayout will unmount');

    Messenger.off(MessengerEvents.AVENUE_RESET, this.moveToTop);
    Messenger.off(MessengerEvents.ITEM_CLICKED, this.executeItemClick);
    Messenger.off(MessengerEvents.MANAGE_SUPER_STREAM, this.manageSuperStream);
    Messenger.off(MessengerEvents.MOVE_TO_TOP, this.moveToTop);
    Messenger.off(MessengerEvents.OPEN_EXPLORE_MODAL, this.openExploreModal);
    Messenger.off(MessengerEvents.OPEN_GLOBAL_SETTINGS, this.openGlobalSettingsScreen);
    Messenger.off(MessengerEvents.OPEN_NPVR_MANAGEMENT_SCREEN, this.openNpvrManagementScreen);
    Messenger.off(MessengerEvents.OPEN_PLAYER, this.openPlayer);
    Messenger.off(MessengerEvents.OPEN_TILE_TYPES_AVENUE, this.openTileTypesAvenue);
    Messenger.off(MessengerEvents.RESTORE_POSITION, this.restorePosition);
    Messenger.off(MessengerEvents.SECTION_SLIDING_UPDATE, this.updateSectionSliding);

    if (document !== undefined) {
      document.removeEventListener('visibilitychange', this.checkVisibility, { passive: true });
    }
  }

  // Debug functions only accessible through browser's console
  setDebugFunctions: () => void = () => {
    const { isDebugModeEnabled, localClearWishlist, localSendV8MetadataRequest, localUpdateSetting } = this.props;

    if (isDebugModeEnabled) {
      showEE();
      logDebug('Debug mode ON');
      window.dbg = {
        changeLanguage: (l: string, callback?: BasicFunction) => i18next.changeLanguage(l, callback),
        checkVersion: () => Messenger.emit(MessengerEvents.CHECK_VERSION, true),
        deleteWishlistFromCloud: localClearWishlist,
        emit: (eventName: string, ...args: Array<any>) => Messenger.emit(eventName, ...args),
        flushDataCollector: () => Messenger.emit(MessengerEvents.FLUSH_COLLECTOR),
        getAssetMetadata: (assetId: string) =>
          localSendV8MetadataRequest(assetId, assetId.startsWith('series://') ? METADATA_KIND_SERIES : METADATA_KIND_PROGRAM).then((response) => logInfo(response)),
        getLogLevel: () => log.getLevel(),
        notifyError: (msg, options) =>
          Messenger.emit(MessengerEvents.NOTIFY_ERROR, msg, {
            autoClose: false,
            ...options,
          }),
        notifyInfo: (msg, options) =>
          Messenger.emit(MessengerEvents.NOTIFY_INFO, msg, {
            autoClose: false,
            ...options,
          }),
        notifySuccess: (msg, options) =>
          Messenger.emit(MessengerEvents.NOTIFY_SUCCESS, msg, {
            autoClose: false,
            ...options,
          }),
        notifyWarning: (msg, options) =>
          Messenger.emit(MessengerEvents.NOTIFY_WARNING, msg, {
            autoClose: false,
            ...options,
          }),
        now: (inMs: boolean) => (inMs ? AccurateTimestamp.now() : AccurateTimestamp.nowAsIsoString()),
        openNpvr: this.openNpvrManagementScreen,
        refreshAuthToken: () => Messenger.emit(MessengerEvents.REFRESH_AUTHENTICATION_TOKEN),
        refreshHub: () => Messenger.emit(MessengerEvents.REFRESH_HUB),
        refreshNpvr: this.refreshNpvr,
        setSetting: (name: string, value: SettingValue) => (Setting.isValid(name) ? localUpdateSetting(Setting.cast(name), value) : logError(`Unknown setting name "${name}"`)),
        showChargebeeInfo: showChargebeeDebug,
        showFinishingLivePrograms: (itemCountPerPage?: number, maxTime?: number, maxItems?: number) =>
          Messenger.emit(MessengerEvents.DEBUG_SHOW_FINISHING_LIVE_PROGRAMS, itemCountPerPage, maxTime, maxItems),
        showHotKeys: HotKeys.listHotKeys,
      };
    } else {
      logInfo('Debug mode OFF');
      window.dbg = null;
    }
  };

  handleOnLogoLoaded: () => void = () => {
    this.setState({ isHeaderLoaded: true });
  };

  checkVisibility: () => void = () => {
    if (isTabHidden()) {
      Messenger.emit(MessengerEvents.VIDEO_CAROUSEL_TRAILER_HIDDEN);
    } else {
      Messenger.emit(MessengerEvents.VIDEO_CAROUSEL_TRAILER_VISIBLE);
    }
  };

  handleOnScroll: () => void = () => {
    const { isInTheaterMode } = this.props;
    const { mainLayout } = this;

    if (isInTheaterMode || mainLayout === null) {
      return;
    }

    this.setState({ isScrolling: mainLayout.scrollTop >= SCROLL_THRESHOLD });
  };

  enterTheaterMode: (item: EXTENDED_ITEM | ShakaOfflineContent) => void = (item) => {
    const { localEnterTheaterMode } = this.props;

    Messenger.emit(MessengerEvents.VIDEO_CAROUSEL_TRAILER_HIDDEN);

    const extendedItem: EXTENDED_ITEM | null = item.type ? (item as EXTENDED_ITEM) : null;
    const offlineContent: ShakaOfflineContent | null = item.offlineUri ? (item as ShakaOfflineContent) : null;

    // Close any open modal but save it for reopening after player exited if it's a card
    this.closeModal(true);
    this.storePosition().then(() => {
      this.setState({ playerItem: extendedItem, playerOfflineContent: offlineContent });
      localEnterTheaterMode();
    });
  };

  exitTheaterMode: () => void = () => {
    const { localExitTheaterMode, localReopenModalCard } = this.props;
    const { playerItem } = this.state;

    this.setState({ playerItem: null, playerOfflineContent: null });
    localExitTheaterMode();

    // Check if player was opened from a card and if an expanded item index has been saved
    const data = LocalStorageManager.loadObject(StorageKeys.CardExpandedItemIndex, null);
    if (data && playerItem?.openFromCard) {
      data.playerExit = true;
      LocalStorageManager.save(StorageKeys.CardExpandedItemIndex, data);
    }

    // Reopen last modal card if any
    localReopenModalCard();
  };

  handleTheaterModeExited: () => void = () => {
    this.restorePosition();
    Messenger.emit(MessengerEvents.VIDEO_CAROUSEL_TRAILER_VISIBLE);
  };

  moveToTop: (savePosition?: boolean) => void = (savePosition = false) => {
    const { mainLayout } = this;

    if (mainLayout === null) {
      return;
    }

    const promise = savePosition ? this.storePosition() : Promise.resolve();
    promise.then(() => (mainLayout.scrollTop = 0));
  };

  buildPreviousPositionKey: () => string = () => {
    const { focusedAvenue } = this.props;

    if (!focusedAvenue) {
      return '';
    }

    return `${focusedAvenue.index}-${focusedAvenue.hubItem?.id ?? 'na'}-${focusedAvenue.searchString ?? 'na'}`;
  };

  storePosition: () => Promise<void> = () => {
    const { mainLayout } = this;

    return new Promise((resolve) => {
      this.setState({ previousVerticalPosition: { key: this.buildPreviousPositionKey(), scroll: mainLayout?.scrollTop ?? 0 } }, resolve);
    });
  };

  restorePosition: () => void = () => {
    const { previousVerticalPosition } = this.state;
    const { mainLayout } = this;

    if (mainLayout === null) {
      return;
    }

    if (previousVerticalPosition) {
      const { key, scroll } = previousVerticalPosition;
      // Check if it's the same avenue before restoring position
      if (key === this.buildPreviousPositionKey()) {
        // Wait a little bit before actually restoring position while everything loads up (layout shift, etc.)
        setTimeout(() => {
          mainLayout.scrollTop = scroll;
          this.setState({ previousVerticalPosition: null });
        }, VERTICAL_POSITION_RESTORE_DELAY);
        return;
      }
    }

    // In all other cases, move to top
    mainLayout.scrollTop = 0;
  };

  openCard: (fullItem: EXTENDED_ITEM, urlDefinition: ?NETGEM_API_V8_URL_DEFINITION, previousCard: ?CARD_DATA_MODAL_TYPE) => void = (fullItem, urlDefinition, previousCard) => {
    const { isUnlockedAdultContent, item, programMetadata, seriesMetadata, tileType } = fullItem;

    Messenger.emit(MessengerEvents.OPEN_CARD, {
      isUnlockedAdultContent,
      item,
      previousCard,
      programMetadata,
      seriesMetadata,
      tileType,
      urlDefinition,
    });
  };

  loadHubFromUrlDefinition: (item: NETGEM_API_V8_FEED_ITEM, urlDefinition: NETGEM_API_V8_URL_DEFINITION) => void = (item, urlDefinition) => {
    const { focusedAvenue, localNavigateToAvenue, localSendV8CustomStrategyRequest } = this.props;
    const { previousVerticalPosition } = this.state;
    const {
      abortController: { signal },
      exploreAvenueIndex,
    } = this;

    // Reset avenue, then retrieve hub from platform
    this.setState({ ...LoadedHeaderState, previousVerticalPosition }, () => {
      localSendV8CustomStrategyRequest(urlDefinition, item, signal)
        .then((response: NETGEM_API_V8_REQUEST_RESPONSE) => {
          const hub: NETGEM_API_V8_HUB = getValidHub(response.result);
          // If explore avenue index is set, use it, else use index of currently focused index (should always be defined)
          const avenueIndex = exploreAvenueIndex ?? focusedAvenue?.index ?? NO_AVENUE_INDEX;
          const avenueType = exploreAvenueIndex ? AvenueType.Explore : (focusedAvenue?.type ?? AvenueType.Regular);
          localNavigateToAvenue(avenueIndex, avenueType, item, hub);
        })
        .catch((error) => ignoreIfAborted(signal, error));
    });
  };

  openPlayer: (item: EXTENDED_ITEM | ShakaOfflineContent) => void = (item) => {
    this.enterTheaterMode(item);
  };

  executeItemClick: (fullItem: EXTENDED_ITEM) => void = (fullItem) => {
    const { defaultOnItemClick } = this.props;
    const { cardData, item, onItemClick: fullItemOnItemClick } = fullItem;
    const localOnItemClick = fullItemOnItemClick ?? defaultOnItemClick;

    if (!item || !localOnItemClick) {
      // No item (only for trailers, but this code is not used for trailers) or no action defined
      return;
    }

    const onItemClick = getOnItemClick(fullItem, localOnItemClick);

    if (onItemClick === null) {
      // No action found
      return;
    }

    this.executeAction(fullItem, onItemClick, cardData);
  };

  executeAction: (fullItem: EXTENDED_ITEM, onItemClick: NetgemApiV8WidgetItemClick, cardData: ?CARD_DATA_MODAL_TYPE) => void = (fullItem, onItemClick, cardData) => {
    const { action, params } = onItemClick;

    if (action === NetgemApiV8WidgetItemClickActionKind.Card && params?.uri !== undefined) {
      // Open card
      this.openCard(fullItem, params as Object as NETGEM_API_V8_URL_DEFINITION, cardData);
    } else if (action === NetgemApiV8WidgetItemClickActionKind.Hub && params?.uri !== undefined) {
      // Open hub (channel, channel group, universe)
      const { item } = fullItem;
      this.storePosition().then(() => this.loadHubFromUrlDefinition(item, params as Object as NETGEM_API_V8_URL_DEFINITION));
    } else if (action === NetgemApiV8WidgetItemClickActionKind.Player) {
      // Open player
      this.openPlayer(fullItem);
    }
  };

  openExploreModal: (exploreAvenueIndex: number) => void = (exploreAvenueIndex) => {
    this.loadAvenue(exploreAvenueIndex, AvenueType.Explore);
  };

  closeModal: (shouldBeReopenedLater?: boolean) => void = (shouldBeReopenedLater) => {
    const { localHideModal, openModals } = this.props;

    if (openModals.length === 0) {
      // No card open
      return;
    }

    localHideModal(shouldBeReopenedLater);
  };

  // Player regularly exited
  playerCloseCallback: () => void = () => {
    this.exitTheaterMode();
    this.refreshNpvr();
  };

  refreshNpvr: () => void = () => {
    Messenger.emit(MessengerEvents.REFRESH_NPVR);
  };

  loadAvenueFromId: (avenueId: string) => boolean = (avenueId) => {
    const { avenueList, localNavigateToAvenue } = this.props;

    const avenue = getAvenueFromId(avenueList, avenueId);

    if (!avenue) {
      return false;
    }

    const { index, type } = avenue;
    localNavigateToAvenue(index, type);
    this.loadAvenue(index, type);

    return true;
  };

  loadAvenue: (index: number, type: AvenueType, hasBackButton?: boolean, hubItem?: NETGEM_API_V8_FEED_ITEM, hub?: NETGEM_API_V8_HUB) => void = (index, type, hasBackButton, hubItem, hub) => {
    const { clearDisplayPaywallSubscription, displaySubscriptionPaywall, focusedAvenue, isRegisteredAsGuest, localResetGridSectionId, localShowAvenueModal } = this.props;
    const { isDeeplinkHandled } = this;

    // Handle deeplink
    if (!isDeeplinkHandled && this.checkDeeplink()) {
      return;
    }

    let localHub = hub;
    let localHubItem = hubItem;

    if (type === AvenueType.Explore && !hub && focusedAvenue && hasPendingOperation()) {
      // Coming back to a hidden avenue after having stored a pending operation: let's use previous hub and hubItem
      ({ hub: localHub, hubItem: localHubItem } = focusedAvenue);
    }

    if (type === AvenueType.Explore && !localHub) {
      // Display EXPLORE avenue in popup
      this.exploreAvenueIndex = index;
      Messenger.once(MessengerEvents.MODAL_CLOSED, () => {
        this.exploreAvenueIndex = null;
      });

      localShowAvenueModal(
        {
          hasBackButton,
          hub: localHub,
          hubItem: localHubItem,
          index,
          isInExploreModal: type === AvenueType.Explore && !localHub,
          type,
        },
        Localizer.localize('accessibility.modal.avenue_explore'),
      );
    } else {
      // Display selected avenue in regular avenue container
      localResetGridSectionId();
      this.closeModal();
      this.exploreAvenueIndex = null;

      if (hasBackButton) {
        // Going to hidden avenue
        this.moveToTop();
      } else {
        // Coming back from hidden avenue
        this.restorePosition();
      }

      const view = (
        <AvenueView hasBackButton={hasBackButton} hub={localHub} hubItem={localHubItem} index={index} isInExploreModal={type === AvenueType.Explore && !localHub} key='avenue' type={type} />
      );

      this.setState({ child: [view] }, () => {
        if (displaySubscriptionPaywall) {
          // Subscription paywall should be displayed ("?subscribe" in URL)
          clearDisplayPaywallSubscription();
          Messenger.emit(MessengerEvents.SHOW_SUBSCRIBE);
        } else if (!isRegisteredAsGuest) {
          Messenger.emit(MessengerEvents.CHECK_PENDING_OPERATION);
        }
      });
    }
  };

  checkDeeplink: () => boolean = () => {
    const { deeplink } = this.props;

    this.isDeeplinkHandled = true;

    if (!deeplink) {
      return false;
    }

    const { searchParams } = new URL(deeplink);

    for (const [parameter, value] of searchParams.entries()) {
      const decodedValue = value ? decodeURIComponent(value) : null;

      switch (parameter) {
        case 'npvr':
          this.openNpvrManagementScreen();
          return true;

        case 'settings':
          this.openGlobalSettingsScreen(decodedValue);
          return true;

        case 'avenue':
          if (decodedValue) {
            return this.loadAvenueFromId(decodedValue);
          }
          return false;

        case 'search':
          if (decodedValue) {
            this.handleOnSearch(decodedValue);
            return true;
          }
          return false;

        case 'program':
          // VOD or TV (scheduled event or catchup)
          if (decodedValue) {
            this.openDeeplinkCard(ItemType.Program, decodedValue);
          }
          return false;

        case 'series':
          // VOD or TV (scheduled event or catchup)
          if (decodedValue) {
            this.openDeeplinkCard(ItemType.Series, decodedValue);
          }
          return false;

        default:
      }
    }

    return false;
  };

  getMatchingRequest: (kind: string) => FeedRequestFunction = (kind) => {
    const { localSendV8LocationCatchupForAssetRequest, localSendV8LocationEpgForAssetRequest, localSendV8LocationVodForAssetRequest } = this.props;

    switch (kind) {
      case ItemLocationKind.ScheduledEvent:
        return localSendV8LocationEpgForAssetRequest;
      case ItemLocationKind.Catchup:
        return localSendV8LocationCatchupForAssetRequest;
      default:
        return localSendV8LocationVodForAssetRequest;
    }
  };

  openDeeplinkCard: (type: ItemType, deeplinkParameter: string) => void = (type, deeplinkParameter) => {
    const { localSendV8AssetIdForDeeplinkRequest } = this.props;
    const {
      abortController: { signal },
    } = this;

    const token = signal;

    localSendV8AssetIdForDeeplinkRequest(type, deeplinkParameter, token)
      .then(({ id, kind }) => {
        const localKinds = Array.isArray(kind) && kind.length > 0 ? kind : [ItemLocationKind.VOD];

        const promises: Array<Promise<FeedRequestResponseType>> = localKinds.map((k) => {
          const request = this.getMatchingRequest(k);
          return request(id, AccurateTimestamp.now(), MILLISECONDS_PER_WEEK, undefined, token);
        });

        return Promise.allSettled(promises).then((results: AllSettledPromises<FeedRequestResponseType>) => {
          const item = getItem(results);

          if (item) {
            // Prop "type" is mandatory, although it's not used when opening a card
            this.executeItemClick({ item, type: ExtendedItemType.VOD });
          }
        });
      })
      .catch((error) => ignoreIfAborted(signal, error));
  };

  openGlobalSettingsScreen: (initialSection: ?string) => void = (initialSection) => {
    const { localNavigateToAvenue } = this.props;

    this.closeModal();
    localNavigateToAvenue(NO_AVENUE_INDEX, AvenueType.Other);
    this.moveToTop();

    this.setState({
      child: [
        <ErrorBoundary componentName='Settings' key='globalSettings'>
          <React.Suspense fallback={null}>
            <AppSettings initialSection={initialSection} key='globalSettings' />
          </React.Suspense>
        </ErrorBoundary>,
      ],
    });
  };

  openNpvrManagementScreen: (scheduledRecordingId: ?string, failedRecordingId: ?string, warningScheduledEventId: ?string) => void = (
    scheduledRecordingId,
    failedRecordingId,
    warningScheduledEventId,
  ) => {
    const { isNpvrFeatureEnabled, localNavigateToAvenue } = this.props;

    if (!isNpvrFeatureEnabled) {
      return;
    }

    this.closeModal();
    localNavigateToAvenue(NO_AVENUE_INDEX, AvenueType.Other);

    this.setState({
      child: [
        <ErrorBoundary componentName='Recordings' key='npvrQuota'>
          <React.Suspense fallback={null}>
            <NpvrManagement focusedExistingRecordingId={failedRecordingId} focusedFutureRecordingId={warningScheduledEventId} focusedScheduledRecordingId={scheduledRecordingId} key='npvrQuota' />
          </React.Suspense>
        </ErrorBoundary>,
      ],
    });
  };

  openTileTypesAvenue: (avenueKey: string) => void = (avenueKey) => {
    const { localSendV8HubRequest } = this.props;
    const {
      abortController: { signal },
    } = this;

    localSendV8HubRequest(avenueKey, signal).then((hub) => {
      const avenue = hub.find(({ id }) => id === 'tile_types_avenue');
      if (avenue) {
        this.loadAvenue(NO_AVENUE_INDEX, AvenueType.Other, false, undefined, [avenue]);
      } else {
        logWarning('Tile types avenue not found (id: tile_types_avenue)');
      }
    });
  };

  openGridAvenue: (avenueKey: string) => void = (avenueKey) => {
    const { localSendV8HubRequest } = this.props;
    const {
      abortController: { signal },
    } = this;

    localSendV8HubRequest(avenueKey, signal).then((hub) => {
      const avenue = hub.find(({ id }) => id === 'tile_types_avenue_grid');
      if (avenue) {
        this.loadAvenue(NO_AVENUE_INDEX, AvenueType.Other, false, undefined, [avenue]);
      } else {
        logWarning('Tile types avenue not found (id: tile_types_avenue_grid)');
      }
    });
  };

  manageSuperStream: () => void = () => {
    const { guruWebsiteUrl, userInfo } = this.props;

    if (process.env.REACT_APP_ID === APPLICATION_ID.MyVideofutur || hasPods(userInfo)) {
      // Open Guru website
      if (guruWebsiteUrl) {
        openFooterPage(guruWebsiteUrl, 'wifiSuperStream');
      } else {
        logWarning('Missing Guru website URL');
      }
    } else {
      Messenger.emit(MessengerEvents.SHOW_PODS);
    }
  };

  handleOnSearch: (searchString: string, isFocusHidden?: boolean) => void = (searchString, isSearchFocusHidden = true) => {
    const { localNavigateToAvenue, localSearch } = this.props;
    const { previousVerticalPosition } = this.state;
    const {
      abortController: { signal },
    } = this;

    // Reset avenue, then retrieve hub from platform
    this.setState({ ...LoadedHeaderState, previousVerticalPosition }, () => {
      localSearch(signal)
        .then((hub: NETGEM_API_V8_HUB) => {
          saveSearchString(searchString);
          localNavigateToAvenue(NO_AVENUE_INDEX, AvenueType.Search, undefined, hub, searchString, isSearchFocusHidden);
        })
        .catch((error) => ignoreIfAborted(signal, error));
    });
  };

  updateSectionSliding: (isSliding: boolean) => void = (isSliding) => {
    this.isSectionSliding = isSliding;
  };

  checkMousePosition: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => boolean = (event) => {
    const { nativeEvent } = event;
    const [, elt] = nativeEvent.composedPath();

    if (!(elt instanceof HTMLElement)) {
      return false;
    }

    const { classList, tagName } = elt;

    return tagName.toUpperCase() === 'DIV' && classList.contains('sectionCarousel');
  };

  handleOnClick: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => void = (event) => {
    const {
      localUpdateVideoCarouselPaused,
      localUpdateVideoCarouselPlaying,
      videoCarousel: { isPlaying },
    } = this.props;

    if (!this.checkMousePosition(event)) {
      return;
    }

    if (isPlaying) {
      localUpdateVideoCarouselPaused();
    } else {
      localUpdateVideoCarouselPlaying();
    }
  };

  handleOnDoubleClick: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => void = (event) => {
    const { isSectionSliding } = this;

    if (isSectionSliding) {
      return;
    }

    if (!this.checkMousePosition(event)) {
      return;
    }

    // Notify video carousel to go fullscreen
    Messenger.emit(MessengerEvents.VIDEO_CAROUSEL_TOGGLE_FULLSCREEN);
  };

  renderWorkingZoneContent: () => React.Node = () => {
    const { hideContent, isInTheaterMode } = this.props;
    const { child } = this.state;

    if (hideContent || isInTheaterMode) {
      return null;
    }

    return (
      <>
        <ImageCarousel />
        <VideoCarousel />
        {child.length > 0 ? child : null}
      </>
    );
  };

  render(): React.Node {
    const { isInTheaterMode } = this.props;
    const { isHeaderLoaded, isScrolling, playerItem, playerOfflineContent } = this.state;

    return (
      <>
        <div
          className={clsx('mainLayout', isInTheaterMode && 'theaterMode')}
          onScroll={this.handleOnScroll}
          ref={(instance: HTMLElement | null) => {
            this.mainLayout = instance;
          }}
        >
          <LandmarkNavigation />
          <Header isScrolling={isScrolling} onLogoLoaded={this.handleOnLogoLoaded} onSearch={this.handleOnSearch} />
          <main className={clsx('workingZone', isInTheaterMode && 'theaterMode')} id='main-content' onClick={this.handleOnClick} onDoubleClick={this.handleOnDoubleClick}>
            {this.renderWorkingZoneContent()}
            <Footer isVisible={isHeaderLoaded} />
          </main>
        </div>
        {playerItem || playerOfflineContent ? <Player closeCallback={this.playerCloseCallback} initialPlayerItem={playerItem} playerOfflineContent={playerOfflineContent} /> : null}
      </>
    );
  }
}

const mapStateToProps = (state: CombinedReducers): ReduxMainLayoutReducerStateType => {
  return {
    avenueList: state.ui.avenueList,
    deeplink: state.ui.deeplink,
    defaultOnItemClick: state.ui.defaultActions ? state.ui.defaultActions.onItemClick : null,
    displaySubscriptionPaywall: state.ui.displaySubscriptionPaywall,
    focusedAvenue: state.ui.navigationStack[state.ui.navigationStack.length - 1],
    guruWebsiteUrl: state.appConfiguration.configuration.guruWebsiteUrl ?? '',
    isDebugModeEnabled: state.appConfiguration.isDebugModeEnabled,
    isInTheaterMode: state.ui.isInTheaterMode,
    isNpvrFeatureEnabled: state.appConfiguration.features[FEATURE_NPVR],
    isRegisteredAsGuest: state.appRegistration.registration === RegistrationType.RegisteredAsGuest,
    openModals: state.modal.openModals,
    userInfo: state.appRegistration.userInfo,
    videoCarousel: state.ui.videoCarousel,
  };
};

const mapDispatchToProps = (dispatch: Dispatch): ReduxMainLayoutDispatchToPropsType => {
  return {
    clearDisplayPaywallSubscription: () => dispatch(updateDisplayPaywallSubscription(false)),

    localClearWishlist: (): Promise<void> => dispatch(clearWishlist()),

    localEnterTheaterMode: () => dispatch(enterTheaterMode()),

    localExitTheaterMode: () => dispatch(exitTheaterMode()),

    localHideModal: (shouldBeReopenedLater?: boolean): void => dispatch(hideModal(shouldBeReopenedLater)),

    localNavigateToAvenue: (index: number, type: AvenueType, hubItem?: NETGEM_API_V8_FEED_ITEM, hub?: NETGEM_API_V8_HUB, searchString?: string, isSearchFocusHidden?: boolean) =>
      dispatch(navigateToAvenue(index, type, hubItem, hub, searchString, isSearchFocusHidden)),

    localReopenModalCard: (): void => dispatch(reopenModalCard()),

    localResetGridSectionId: (): void => dispatch(resetGridSectionId()),

    localResetSectionPageIndices: (): void => dispatch(resetSectionPageIndices()),

    localSearch: (signal?: AbortSignal): Promise<NETGEM_API_V8_HUB> => dispatch(search(signal)),

    localSendV8AssetIdForDeeplinkRequest: (type: ItemType, deeplinkParameter: string, signal?: AbortSignal): Promise<NETGEM_API_V8_DEEPLINK_RESPONSE> =>
      dispatch(sendV8AssetIdForDeeplinkRequest(type, deeplinkParameter, signal)),

    localSendV8CustomStrategyRequest: (urlDefinition: NETGEM_API_V8_URL_DEFINITION, item: NETGEM_API_V8_FEED_ITEM, signal?: AbortSignal): Promise<NETGEM_API_V8_REQUEST_RESPONSE> =>
      dispatch(sendV8CustomStrategyRequest(urlDefinition, item, signal)),

    localSendV8HubRequest: (hubKey: string, signal?: AbortSignal): Promise<NETGEM_API_V8_HUB> => dispatch(sendV8HubRequest(hubKey, signal)),

    localSendV8LocationCatchupForAssetRequest: (assetId: string, startDate: number, range: number, channelIds?: Array<string>, signal?: AbortSignal): Promise<FeedRequestResponseType> =>
      dispatch(sendV8LocationCatchupForAssetRequest(assetId, startDate, range, channelIds, signal)),

    localSendV8LocationEpgForAssetRequest: (assetId: string, startDate: number, range: number, channelIds?: Array<string>, signal?: AbortSignal): Promise<FeedRequestResponseType> =>
      dispatch(sendV8LocationEpgForAssetRequest(assetId, startDate, range, channelIds, signal)),

    localSendV8LocationVodForAssetRequest: (assetId: string, startDate: number, range: number, channelIds?: Array<string>, signal?: AbortSignal): Promise<FeedRequestResponseType> =>
      dispatch(sendV8LocationVodForAssetRequest(assetId, startDate, range, channelIds, signal)),

    localSendV8MetadataRequest: (assetId: string, type: MetadataKind, signal?: AbortSignal): Promise<NETGEM_API_V8_METADATA> => dispatch(sendV8MetadataRequest(assetId, type, signal)),

    localShowAvenueModal: (avenueData: AVENUE_DATA_MODAL_TYPE, ariaLabel: string): void => dispatch(showAvenueModal(avenueData, ariaLabel)),

    localUpdateSetting: (setting?: Setting, value: SettingValue): void => dispatch(updateSetting(setting, value)),

    localUpdateVideoCarouselPaused: () => dispatch(updateVideoCarouselPaused()),

    localUpdateVideoCarouselPlaying: () => dispatch(updateVideoCarouselPlaying()),
  };
};

const MainLayout: React.ComponentType<MainLayoutPropType> = connect(mapStateToProps, mapDispatchToProps)(MainLayoutView);

export default MainLayout;
